import { defineStore } from "pinia";
import type { PaginationAttributes } from "@/interface/pagination_interface";
import { ExportEnquiryRepository } from "@/repository/export_asset_repository";
import type { ValidBookingIds } from "@/interface/export/export_booking_detail_interface";
import { appButtonClick } from "@/utils/analytics";
import { useExportLocationStore } from "@/store/export/export_location_store";
import { useExportSearchIdsStore } from "@/store/export/export_id_store";

const locationStore = useExportLocationStore();
const searchIdStore = useExportSearchIdsStore();

export const useExportBookingDetailStore = defineStore("useExportBookingDetailStore", {
  state: (): {
    originalContainers: ValidBookingIds[];
    filteredExportAsset: ValidBookingIds[];
    originalBookings: ValidBookingIds[];
    filterBooking: ValidBookingIds[];
    noResult: string[];
    selectedBookingNumber: string;
    isContainer: boolean;
    columnNames: [];
    bookingPagination: PaginationAttributes;
    containerPagination: PaginationAttributes;
    isLoading: boolean;
    error: string | null;
  } => ({
    originalContainers: [] as ValidBookingIds[],
    filteredExportAsset: [] as ValidBookingIds[],
    originalBookings: [] as ValidBookingIds[],
    filterBooking: [] as ValidBookingIds[],
    noResult: [] as string[],
    selectedBookingNumber: "",
    isContainer: false,
    columnNames: [] as [],
    bookingPagination: {
      totalPages: 0,
      page: 1,
      pageSize: 25,
      totalRecords: 0
    },
    containerPagination: {
      totalPages: 0,
      page: 1,
      pageSize: 25,
      totalRecords: 0
    },
    isLoading: false,
    error: null
  }),
  persist: false,
  getters: {
    // get invalid ids
    getInvalidIds(): string[] {
      return this.noResult;
    }
  },
  actions: {
    // Making api call for the contianer history
    async fetchExportAssets(): Promise<void> {
      this.resetStore();
      try {
        this.isLoading = true;
        const service = new ExportEnquiryRepository();
        const containers = await service.getEnquiryDetail();
        appButtonClick("Export Enquiry", "Search Results Count", locationStore.terminalSelected.oneStreamCode, {
          searchedContainerCount: searchIdStore.exportSearchIds.length,
          validContainersCount: containers.validIds.length,
          invalidContainersCount: containers.invalidIds.length,
          validBookingsCount: containers.bookingIds.length,
          responseCode: "200",
          ...locationStore.anayticsTerminalDetails
        });
        this.originalContainers = containers.validIds;
        this.filteredExportAsset = containers.validIds;
        this.originalBookings = containers.bookingIds;
        this.filterBooking = containers.bookingIds;
        if (containers.bookingIds.length === 0) {
          this.isContainer = true; // If no bookings, show containers by default
        }

        this.noResult = containers.invalidIds;
        // Invalid ids that are not found in the result
        this.isLoading = false;
        this.updatePaginationCount();
      } catch (e: any) {
        appButtonClick("Export Enquiry", "Search Results Count", locationStore.terminalSelected.oneStreamCode, {
          searchedContainerCount: searchIdStore.exportSearchIds.length,
          validContainersCount: 0,
          invalidContainersCount: 0,
          validBookingsCount: 0,
          responseCode: e || "500",
          ...locationStore.anayticsTerminalDetails
        });
        this.isLoading = false;
        this.error = e;
      }
    },
    // Filter the import asset by booking number but should not change the original import asset list
    filterByBookingNumber(bookingNumber: string): void {
      const filteredExportAsset = this.originalContainers.filter((item: ValidBookingIds): boolean => {
        return item.bookingNumber === bookingNumber;
      });
      this.filteredExportAsset = filteredExportAsset;
      this.updatePaginationCount();
    },
    // Reset searched filter data
    setInitialState(): void {
      const list = this.originalContainers;
      if (list?.length > 0) {
        this.filteredExportAsset = list;
        this.updatePaginationCount();
      }
    },
    // Search filter for export asset asset
    searchBookingFilterAssets(searchText: string): void {
      this.originalBookings = this.originalBookings.filter((asset: ValidBookingIds): boolean => {
        return (
          asset.bookingNumber?.toLowerCase().includes(searchText) ||
          asset.vesselName?.toLowerCase().includes(searchText) ||
          asset.shippingLineOperator?.toLowerCase().includes(searchText)
        );
      });
      this.updatePaginationCount();
    },

    // Search filter for export asset asset
    searchContainerFilterAssets(searchText: string): void {
      this.filteredExportAsset = this.originalContainers.filter((asset: ValidBookingIds): boolean => {
        return (
          asset.containerNumber?.toLowerCase().includes(searchText) ||
          asset.bookingNumber?.toLowerCase().includes(searchText) ||
          asset.vesselName?.toLowerCase().includes(searchText) ||
          asset.shippingLineOperator?.toLowerCase().includes(searchText)
        );
      });
      this.updatePaginationCount();
    },

    toggleContainer(): void {
      this.isContainer = !this.isContainer;
    },

    // Remove booking number filter
    removeBookingNumberFilter(): void {
      this.selectedBookingNumber = "";
      this.filteredExportAsset = [...this.originalContainers];
      this.updatePaginationCount();
    },

    // Reset the import asset list
    resetExportAssetList(): void {
      this.filteredExportAsset = [];
      this.updatePaginationCount();
    },

    // Set the booking number to bookingNumber
    setBookingNumber(bookingNumber: string): void {
      this.selectedBookingNumber = bookingNumber;
    },

    // Clear invalid ids
    clearInvalidIds(): void {
      this.noResultIds = [];
    },

    // Updating the pagination count
    updatePaginationCount(): void {
      this.bookingPagination = {
        ...this.bookingPagination,
        totalPages: Math.ceil(this.filterBooking.length / this.bookingPagination.pageSize),
        totalRecords: this.filterBooking.length,
        pageSize: 25,
        page: 1
      };
      this.containerPagination = {
        ...this.containerPagination,
        totalPages: Math.ceil(this.filteredExportAsset.length / this.containerPagination.pageSize),
        totalRecords: this.filteredExportAsset.length,
        pageSize: 25,
        page: 1
      };
    },

    updateContainerPaginationCount(): void {
      this.containerPagination = {
        ...this.containerPagination,
        totalPages: Math.ceil(this.filteredExportAsset.length / this.containerPagination.pageSize),
        totalRecords: this.filteredExportAsset.length
      };
    },

    updateBookingPaginationCount(): void {
      this.bookingPagination = {
        ...this.bookingPagination,
        totalPages: Math.ceil(this.filterBooking.length / this.bookingPagination.pageSize),
        totalRecords: this.filterBooking.length
      };
    },

    resetError(): void {
      this.error = null;
    },

    // Reset the store
    resetStore(): void {
      this.originalContainers = [];
      this.filteredExportAsset = [];
      this.bookings = [];
      this.noResult = [];
      this.error = "";
      this.isLoading = false;
      this.bookingPagination = {
        totalPages: 0,
        page: 1,
        pageSize: 25,
        totalRecords: 0
      };
      this.containerPagination = {
        totalPages: 0,
        page: 1,
        pageSize: 25,
        totalRecords: 0
      };
      this.originalContainers = [];
      this.filteredExportAsset = [];
      this.originalBookings = [];
      this.filterBooking = [];
    }
  }
});
